import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { vdjColor, vdjBeatChroma, vdjCameraKick, VdjPost, VdjComfort, VDJ_DROP_FLASH } from './lib/vdjLook.js';
import { loadArtworkTexture } from '../artwork-texture.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';

// Contrat du rig partagé : interdit. Le labyrinthe a sa caméra et sa cible.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'maze',
  family: 'geometric',
  danceability: 0.88,
  energyLevel: 0.86,
  bpmMin: 120,
  bpmMax: 175,
  movement: 0.90,
  tonality: 0.35,
  prefers: { gridStrength: [0.65, 1.0] },
  enabled: true
};

// Maze : une course à la première personne dans un labyrinthe infini aux murs
// de néon. LOOK VDJ, preset party (lib/vdjLook.js). Techno, hard techno,
// drum'n'bass, dubstep.
//
// LE LABYRINTHE, infini et déterministe. Les cellules font 1 x 1 (hauteur 1).
// Un CARREFOUR tous les 4 cellules dans chaque axe : la cellule (cx, cz) est
//   - un carrefour si cx et cz sont multiples de 4 (toujours ouverte) ;
//   - un couloir si un seul des deux l'est : ouverte si l'ARÊTE du
//     labyrinthe de carrefours qu'elle porte est ouverte ;
//   - pleine sinon (blocs de 3 x 3).
// Le labyrinthe de carrefours : arbre binaire infini (chaque carrefour ouvre
// au NORD ou à l'EST, au hasard du hachage) tressé (chaque arête s'ouvre en
// plus avec la probabilité BRAID) - des boucles, des couloirs latéraux. Le
// hachage est EXACT et partagé JS/GLSL (la permutation mod 289 des bruits
// d'Ashima, entiers sous 2^24, comme Cyberflight) : le CPU lit le même
// labyrinthe que le GPU dessine, et un couloir revu est identique.
//
// LE CHEMIN : à une cellule par temps, la caméra atteint un carrefour à
// CHAQUE PREMIER TEMPS DE MESURE (4 cellules par mesure) ; x2 et x4 dans la
// montée, la vitesse ne change qu'au premier temps, donc la règle tient. Le
// chemin est planifié 32 cellules à l'avance (CPU, une fois par mesure, une
// recherche en profondeur sur les carrefours) : virage permis seulement aux
// carrefours d'un premier temps, ligne droite obligatoire aux autres, et la
// planification doit FINIR en direction du nord ou de l'est. Depuis un
// carrefour, le nord ou l'est est toujours ouvert (l'arête de l'arbre) : un
// plan qui finit ainsi se prolonge toujours, la caméra ne rencontre jamais
// d'impasse. Elle ne quitte jamais l'axe des couloirs (virage : quart de
// cercle de rayon 1/4 dans le carrefour, un demi-temps à vitesse 1) : jamais
// de collision.
//
// LES PANNEAUX : un quad instancié par face de cellule (sol, plafond, 4 murs)
// sur une fenêtre N x N de cellules autour de la caméra, recyclée dans le
// vertex shader (uniform de fenêtre, positions RELATIVES à une origine
// entière). Faces noires opaques (elles masquent les arêtes de derrière),
// arêtes néon par distance en pixels au bord du quad (R1). Chaque mur est un
// ÉCRAN : BARS (égaliseur 16 bandes, crêtes), ARROWS (chevrons dans le sens
// de la course), GRID (points qui s'allument en vague depuis la caméra),
// COVER (la pochette, un panneau par couloir), BLANK. Le motif vient du
// hachage de la face et de la phrase.
// Sol : miroir analytique - une copie des murs et du plafond reflétée sous le
// sol (y -> -y), assombrie avec la profondeur, vue à travers les arêtes du sol.
// Brouillard NOIR par pixel.
//
// LA SALLE DU DROP : le segment parcouru juste après le drop (5 cellules) et
// deux cellules de chaque côté s'ouvrent en une salle 5 x 5 aux murs tous en
// BARS ; le plafond s'ouvre sur le noir (les murs montent) ; puis la course
// reprend. La salle reste là (un seul à la fois) : un couloir revu est le même.
//
// Debug : ?mazepanel=bars|arrows|grid|cover|blank, ?mazeseed=<n>, ?mazestop=1.

const TIER = {
  0: { n: 20, fog: [4.5, 8], mirror: false, samples: 0 },
  1: { n: 28, fog: [7.5, 13], mirror: true, samples: 0 },
  2: { n: 36, fog: [9, 16], mirror: true, samples: 4 }
};
const J = 4;                         // cells between junctions
const BRAID = 0.30;                  // extra edges
const WALL_H = 1.0;
const CAM_Y = 0.5;
const TURN_R = 0.25;                 // corner arc radius (cells): half a beat at rate 1
const TURN_ROLL = 6 * Math.PI / 180;
const TURN_P = 0.70;                 // a possible turn is taken this often
const PLAN_CELLS = 32;
const HALL_HALF = 2;                 // the drop hall: 5 cells across
const PANEL_NAMES = { bars: 0, arrows: 1, grid: 2, cover: 3, blank: 4 };
// the equaliser (Cyberflight's)
const BANDS = 16;
const BAND_CENTRES = [0.12, 0.50, 0.88];
const BAND_SIGMA = 0.26;
const DECORR = 0.10;
const FALL_PER_S = 1.6;
const PEAK_HOLD_S = 0.80;
const PEAK_FALL_PER_S = 0.5;
const KICK_SPIKE = 0.30;
const SYNTH_BPM = 128;
const DIRS = [[1, 0], [0, 1], [-1, 0], [0, -1]];      // E (+x), N (+z), W, S

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}

// --- the exact hash, shared by JS and GLSL --------------------------------------
function m289(x) { return x - 289 * Math.floor((x + 0.5) / 289); }
function perm(x) { return m289((x * 34 + 1) * x); }
function hash(bx, bz, salt) { let p = perm(m289(bx) + salt); p = perm(p + m289(bz)); p = perm(p + 17); return p / 289; }
const HASH_GLSL = `
  float m289(float x) { return x - 289.0 * floor((x + 0.5) / 289.0); }
  float perm(float x) { return m289((x * 34.0 + 1.0) * x); }
  float hsh(vec2 b, float salt) { float p = perm(m289(b.x) + salt); p = perm(p + m289(b.y)); p = perm(p + 17.0); return p / 289.0; }
  float imod(float a, float n) { return a - n * floor((a + 0.5) / n); }
`;

// The maze on the CPU: the same functions as MAZE_GLSL below.
class MazeField {
  constructor(seed) { this.seed = seed; this.hall = null; }
  parentE(X, Z) { return hash(X, Z, this.seed) < 0.5; }
  edgeE(X, Z) { return this.parentE(X, Z) || hash(X, Z, 41 + this.seed) < BRAID; }
  edgeN(X, Z) { return !this.parentE(X, Z) || hash(X, Z, 73 + this.seed) < BRAID; }
  exit(X, Z, d) {
    if (d === 0) return this.edgeE(X, Z);
    if (d === 1) return this.edgeN(X, Z);
    if (d === 2) return this.edgeE(X - 1, Z);
    return this.edgeN(X, Z - 1);
  }
  inHall(cx, cz) { const h = this.hall; return !!h && cx >= h[0] && cx <= h[2] && cz >= h[1] && cz <= h[3]; }
  open(cx, cz) {
    if (this.inHall(cx, cz)) return true;
    const X = Math.floor(cx / J), Z = Math.floor(cz / J), mx = cx - J * X, mz = cz - J * Z;
    if (mx === 0 && mz === 0) return true;
    if (mz === 0) return this.edgeE(X, Z);
    if (mx === 0) return this.edgeN(X, Z);
    return false;
  }
}

const MAZE_GLSL = `
  ${HASH_GLSL}
  uniform float uSeed;
  uniform vec4 uHall;            // absolute cells x0, z0, x1, z1
  uniform float uHallOn;
  float parentE(vec2 M) { return 1.0 - step(0.5, hsh(M, uSeed)); }
  float edgeE(vec2 M) { return max(parentE(M), 1.0 - step(${BRAID.toFixed(2)}, hsh(M, 41.0 + uSeed))); }
  float edgeN(vec2 M) { return max(1.0 - parentE(M), 1.0 - step(${BRAID.toFixed(2)}, hsh(M, 73.0 + uSeed))); }
  float inHall(vec2 c) {
    return uHallOn * step(uHall.x - 0.5, c.x) * step(c.x, uHall.z + 0.5) * step(uHall.y - 0.5, c.y) * step(c.y, uHall.w + 0.5);
  }
  float cellOpen(vec2 c) {
    vec2 M = floor((c + 0.5) / ${J}.0);
    vec2 m = c - ${J}.0 * M;
    float o = 0.0;
    if (m.x < 0.5 && m.y < 0.5) o = 1.0;
    else if (m.y < 0.5) o = edgeE(M);
    else if (m.x < 0.5) o = edgeN(M);
    return max(o, inHall(c));
  }
`;

// --- shaders --------------------------------------------------------------------
const panelVertex = `
  precision highp float;
  attribute vec2 aInst;
  attribute float aFace;           // 0 floor, 1 ceiling, 2 +x, 3 -x, 4 +z, 5 -z
  ${MAZE_GLSL}
  uniform float uN;
  uniform vec2 uWinBase;
  uniform vec2 uOrigin;
  uniform float uPass;             // 0 walls + ceiling, 1 their reflection, 2 floor
  uniform float uPhrase;
  uniform float uForce;            // -1, or a panel kind for every wall
  uniform float uHasCover;
  uniform float uCeilOpen;
  uniform vec2 uFwd;
  varying vec2 vUv;
  varying vec3 vW;
  varying float vKind;
  varying float vDir;
  varying float vFlipU;
  varying float vBandOff;
  vec2 windowBlock(vec2 inst) { return uWinBase + vec2(imod(inst.x - uWinBase.x, uN), imod(inst.y - uWinBase.y, uN)); }
  void main() {
    vec2 b = windowBlock(aInst);
    float f = aFace;
    float show = cellOpen(b);
    show *= uPass > 1.5 ? 1.0 - step(0.5, f) : step(0.5, f);
    vec2 nb = b;
    if (f > 4.5) nb.y -= 1.0; else if (f > 3.5) nb.y += 1.0; else if (f > 2.5) nb.x -= 1.0; else if (f > 1.5) nb.x += 1.0;
    if (f > 1.5) show *= 1.0 - cellOpen(nb);
    float hall = inHall(b);
    // the drop hall: its ceiling opens on the dark, its walls rise
    if (f > 0.5 && f < 1.5) show *= 1.0 - hall * step(0.02, uCeilOpen);
    float H = ${WALL_H.toFixed(2)} * (1.0 + 1.5 * uCeilOpen * hall);
    vec2 rel = b - uOrigin;
    float u = position.x, v = position.y;
    vec3 p;
    if (f < 0.5) p = vec3(rel.x + u, 0.0, rel.y + v);
    else if (f < 1.5) p = vec3(rel.x + u, H, rel.y + v);
    else if (f < 2.5) p = vec3(rel.x + 1.0, v * H, rel.y + u);
    else if (f < 3.5) p = vec3(rel.x, v * H, rel.y + u);
    else if (f < 4.5) p = vec3(rel.x + u, v * H, rel.y + 1.0);
    else p = vec3(rel.x + u, v * H, rel.y);
    vUv = vec2(u, v);
    vFlipU = (f > 2.5 && f < 4.5) ? 1.0 : 0.0;
    // the arrows run along the corridor, in the direction of the run
    vec2 axis = f < 3.5 ? vec2(0.0, 1.0) : vec2(1.0, 0.0);
    float s = dot(axis, uFwd);
    vDir = abs(s) > 0.5 ? sign(s) : 0.0;
    // the panel's kind
    float kind = -1.0;
    if (f > 1.5) {
      vec2 M = floor((b + 0.5) / ${J}.0);
      vec2 m = b - ${J}.0 * M;
      float r = hsh(b, 200.0 + f * 11.0 + mod(uPhrase, 7.0) * 17.0 + uSeed);
      kind = r < 0.30 ? 0.0 : (r < 0.48 ? 1.0 : (r < 0.73 ? 2.0 : 4.0));
      // one cover per corridor: one of its six wall panels
      float corridorX = (m.y < 0.5 && m.x > 0.5) ? 1.0 : 0.0;
      float corridorZ = (m.x < 0.5 && m.y > 0.5) ? 1.0 : 0.0;
      if (corridorX + corridorZ > 0.5) {
        float k = floor(hsh(M, 97.0 + corridorZ * 5.0 + uSeed) * 6.0);
        float cell = 1.0 + floor(k / 2.0), side = mod(k, 2.0);
        float along = corridorX > 0.5 ? m.x : m.y;
        float mySide = corridorX > 0.5 ? (f > 4.5 ? 1.0 : (f > 3.5 ? 0.0 : -1.0)) : (f > 2.5 && f < 3.5 ? 1.0 : (f > 1.5 && f < 2.5 ? 0.0 : -1.0));
        if (abs(along - cell) < 0.5 && abs(mySide - side) < 0.5 && uHasCover > 0.5) kind = 3.0;
      }
      if (uForce > -0.5 && kind != 3.0) kind = uForce;
      if (hall > 0.5) kind = 0.0;
    }
    vKind = kind;
    vBandOff = floor(hsh(b, 230.0 + f + uSeed) * 2.0);
    if (uPass > 0.5 && uPass < 1.5) p.y = -p.y;
    vW = p;
    if (show < 0.5) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); return; }   // degenerate: no area
    gl_Position = projectionMatrix * viewMatrix * vec4(p, 1.0);
  }
`;

const panelFragment = `
  precision highp float;
  uniform float uPass;
  uniform vec3 uAccent;
  uniform vec3 uAccent2;
  uniform float uPulse;
  uniform float uEdgeFlash;
  uniform vec2 uFog;
  uniform sampler2D uLevels;
  uniform sampler2D uCover;
  uniform float uScroll;
  uniform float uBeats;
  uniform float uPanelGain;
  varying vec2 vUv;
  varying vec3 vW;
  varying float vKind;
  varying float vDir;
  varying float vFlipU;
  varying float vBandOff;
  // a stroke of half-width w px at distance d px: 1 inside, AA edge
  float stroke(float d, float w) { return 1.0 - smoothstep(w - 0.6, w + 0.6, d); }
  void main() {
    vec2 fw = max(fwidth(vUv), vec2(1e-5));
    vec2 e = min(vUv, 1.0 - vUv) / fw;
    float d = min(e.x, e.y);
    // the panel's edge (R1): core 1 px each side (2 across the shared edge),
    // sheath to 3 px, faint halo; the core is MIXED over the sheath, kept just
    // under the bloom threshold (Cyberflight: a sum beads in the bloom mips)
    float core = (1.0 - smoothstep(0.55, 1.15, d)) * 0.88;
    float sheath = 1.0 - smoothstep(2.2, 3.4, d);
    float halo = exp(-d / 3.0) * 0.08;
    float facePx = 1.0 / max(fw.x, fw.y);
    float small = smoothstep(4.0, 16.0, facePx);
    float boost = 1.0 + 0.12 * uPulse + 0.9 * uEdgeFlash;
    vec3 glow = (uAccent * 0.62 * sheath + uAccent * halo) * boost;
    vec3 col = mix(glow, vec3(1.0), core * small * min(1.0, 0.75 + 0.25 * boost)) * (0.35 + 0.65 * small);
    // the screen
    float lod = smoothstep(14.0, 40.0, facePx);
    if (vKind > -0.5 && vKind < 3.5 && lod > 0.0) {
      vec2 q = (vUv - 0.1) / 0.8;
      vec2 qw = max(fwidth(q), vec2(1e-5));
      float inside = step(0.0, q.x) * step(q.x, 1.0) * step(0.0, q.y) * step(q.y, 1.0);
      vec3 scr = vec3(0.0);
      if (vKind < 0.5) {
        // BARS: 8 bars, each a band of the equaliser, LED segments, a crest
        float x = q.x * 8.0;
        float i = floor(x), fx = fract(x);
        float band = mod(i * 2.0 + vBandOff, 16.0);
        vec2 lv = texture2D(uLevels, vec2((band + 0.5) / 16.0, 0.5)).rg;
        float bw = 0.32 / (qw.x * 8.0);                       // bar half width, px
        float bx = abs(fx - 0.5) / (qw.x * 8.0);               // px from the bar's axis
        float body = (1.0 - smoothstep(bw - 0.6, bw + 0.6, bx)) * (1.0 - smoothstep(-0.6, 0.6, (q.y - lv.x) / qw.y));
        float seg = smoothstep(0.08, 0.22, fract(q.y * 14.0)) ;
        float crest = stroke(abs(q.y - lv.y) / qw.y, 1.0) * (1.0 - smoothstep(bw - 0.6, bw + 0.6, bx));
        scr = uAccent2 * 0.42 * body * (0.55 + 0.45 * seg) + mix(uAccent2, vec3(1.0), 0.4) * 0.80 * crest;
      } else if (vKind < 1.5) {
        // ARROWS: chevrons scrolling with the run
        float along = vDir != 0.0 ? (vDir > 0.0 ? q.x : 1.0 - q.x) : q.y;
        float across = vDir != 0.0 ? q.y : q.x;
        float c = (along + 0.55 * abs(across - 0.5)) * 2.5 - uScroll;
        float cw = max(fwidth(c), 1e-5);
        float dd = abs(fract(c) - 0.5) / cw;
        scr = uAccent2 * 0.65 * stroke(dd, 2.0) + uAccent2 * 0.12 * exp(-dd / 4.0);
      } else if (vKind < 2.5) {
        // GRID: dots lit by a wave leaving the camera on every beat
        vec2 g = q * vec2(6.0, 4.0);
        vec2 gw = max(fwidth(g), vec2(1e-5));
        vec2 fc = (fract(g) - 0.5) / gw;
        float dot_ = stroke(length(fc), 2.2);
        float dist = length(vW.xz - cameraPosition.xz);
        float wave = pow(0.5 + 0.5 * cos(6.2832 * (uBeats - dist * 0.30)), 8.0);
        scr = uAccent2 * dot_ * (0.10 + 0.70 * wave);
      } else {
        // COVER
        vec2 cq = vec2(vFlipU > 0.5 ? 1.0 - q.x : q.x, q.y);
        scr = texture2D(uCover, clamp(cq, 0.0, 1.0)).rgb * 0.72;
      }
      col += scr * inside * lod * uPanelGain;
    }
    // black fog, per pixel
    float dist = length(vW - cameraPosition);
    col *= 1.0 - smoothstep(uFog.x, uFog.y, dist);
    // the floor mirror: the reflected copy, dimmer with its depth under the floor
    if (uPass > 0.5 && uPass < 1.5) col *= 0.30 * exp(1.6 * vW.y);
    if (uPass > 1.5) col *= 0.75;
    gl_FragColor = vec4(col, 1.0);
  }
`;

const quadVertex = `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;
// the safe zone, per fragment (R19: it follows the cover wherever it is)
const quadFragment = `
  precision highp float;
  uniform sampler2D uScene;
  uniform float uGain;
  uniform float uAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  varying vec2 vUv;
  void main() {
    vec2 s = vec2((vUv.x * 2.0 - 1.0) * uAspect, vUv.y * 2.0 - 1.0);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float sd = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    float att = mix(0.40, 1.0, smoothstep(0.0, uSafeFeather, sd));
    gl_FragColor = vec4(texture2D(uScene, vUv).rgb * uGain * att, 1.0);
  }
`;

// --- geometry: one unit quad, instanced (window cell x 6 faces) -----------------
function panelGeometry(n) {
  const g = new THREE.InstancedBufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute([0, 0, 0, 1, 0, 0, 1, 1, 0, 0, 1, 0], 3));
  g.setIndex([0, 1, 2, 0, 2, 3]);
  const inst = new Float32Array(n * n * 6 * 2), face = new Float32Array(n * n * 6);
  let k = 0;
  for (let j = 0; j < n; j++) for (let i = 0; i < n; i++) for (let f = 0; f < 6; f++) {
    inst[k * 2] = i; inst[k * 2 + 1] = j; face[k] = f; k++;
  }
  g.setAttribute('aInst', new THREE.InstancedBufferAttribute(inst, 2));
  g.setAttribute('aFace', new THREE.InstancedBufferAttribute(face, 1));
  g.instanceCount = n * n * 6;
  return g;
}

const MATS = new Map();
let UNI = null;
let BLANK = null;

export class MazeVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null ? o.qualityTier : 1;
    this._layout = TIER[this._tier] || TIER[1];
    const pn = urlParam('mazepanel');
    this._forcedPanel = pn && PANEL_NAMES[pn] != null ? PANEL_NAMES[pn] : null;
    const sd = parseInt(urlParam('mazeseed'), 10);
    this._seed = Number.isFinite(sd) ? ((sd % 200) + 200) % 200 : 0;
    this._stop = urlParam('mazestop') === '1';
    this._post = new VdjPost({ bloomStrength: 0.30, bloomThreshold: 0.95, vignette: 0.30, grain: 0.010, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 });
    this._comfort = new VdjComfort();
  }

  get postProcessing() { return this._post.value; }

  init(scene, camera) {
    this._scene = scene;
    const L = this._layout, N = L.n;
    this._n = N;
    this._inner = new THREE.Scene();
    this._cam = new THREE.PerspectiveCamera(74, 16 / 9, 0.03, 60);
    if (!UNI) UNI = {
      uN: { value: N }, uWinBase: { value: new THREE.Vector2() }, uOrigin: { value: new THREE.Vector2() },
      uSeed: { value: 0 }, uHall: { value: new THREE.Vector4() }, uHallOn: { value: 0 }, uCeilOpen: { value: 0 },
      uPhrase: { value: 0 }, uForce: { value: -1 }, uHasCover: { value: 0 }, uFwd: { value: new THREE.Vector2(0, 1) },
      uAccent: { value: new THREE.Color(0.2, 0.8, 1.0) }, uAccent2: { value: new THREE.Color(1.0, 0.2, 0.8) },
      uPulse: { value: 0 }, uEdgeFlash: { value: 0 }, uFog: { value: new THREE.Vector2(9, 16) },
      uLevels: { value: null }, uCover: { value: null }, uScroll: { value: 0 }, uBeats: { value: 0 }, uPanelGain: { value: 1 }
    };
    const u = this._u = UNI;
    u.uN.value = N; u.uSeed.value = this._seed; u.uFog.value.set(L.fog[0], L.fog[1]);
    u.uHallOn.value = 0; u.uCeilOpen.value = 0; u.uPhrase.value = 0; u.uForce.value = this._forcedPanel != null ? this._forcedPanel : -1;
    this._levelsData = new Uint8Array(BANDS * 4);
    this._levelsTex = new THREE.DataTexture(this._levelsData, BANDS, 1, THREE.RGBAFormat, THREE.UnsignedByteType);
    this._levelsTex.minFilter = this._levelsTex.magFilter = THREE.NearestFilter;
    this._levelsTex.generateMipmaps = false; this._levelsTex.needsUpdate = true;
    u.uLevels.value = this._levelsTex;
    if (!BLANK) { BLANK = new THREE.DataTexture(new Uint8Array([0, 0, 0, 255]), 1, 1); BLANK.needsUpdate = true; }
    u.uCover.value = BLANK; u.uHasCover.value = 0;

    // three passes over ONE instanced geometry; each material gets its own
    // uPass holder next to the shared ones (three keeps the uniform object)
    const mat = (key, pass, extra) => {
      let m = MATS.get(key);
      if (!m) {
        m = new THREE.ShaderMaterial(Object.assign({
          uniforms: Object.assign({}, u, { uPass: { value: pass } }),
          vertexShader: panelVertex, fragmentShader: panelFragment, side: THREE.DoubleSide
        }, extra || {}));
        MATS.set(key, m);
      }
      return m;
    };
    this._geo = panelGeometry(N);
    this._main = new THREE.Mesh(this._geo, mat('main', 0));
    this._main.frustumCulled = false; this._main.renderOrder = 1;
    this._inner.add(this._main);
    if (L.mirror) {
      this._mirror = new THREE.Mesh(this._geo, mat('mirror', 1));
      this._mirror.frustumCulled = false; this._mirror.renderOrder = 0;
      this._inner.add(this._mirror);
    }
    this._floor = new THREE.Mesh(this._geo, mat('floor', 2, { transparent: true, depthWrite: false, blending: THREE.AdditiveBlending }));
    this._floor.frustumCulled = false; this._floor.renderOrder = 2;
    this._inner.add(this._floor);

    this._rt = new THREE.WebGLRenderTarget(4, 4, { samples: L.samples, type: THREE.UnsignedByteType, colorSpace: THREE.SRGBColorSpace, depthBuffer: true, stencilBuffer: false });
    this._qu = {
      uScene: { value: this._rt.texture }, uGain: { value: 1 },
      uAspect: safeZoneUniforms.uAspect, uSafeCenter: safeZoneUniforms.uSafeCenter,
      uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather
    };
    this._quad = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({
      uniforms: this._qu, vertexShader: quadVertex, fragmentShader: quadFragment, depthTest: false, depthWrite: false }));
    this._quad.frustumCulled = false; this._quad.renderOrder = -3000;
    const self = this;
    this._size = new THREE.Vector2();
    this._quad.onBeforeRender = function (renderer) { self._render(renderer); };
    scene.add(this._quad);

    // the maze and the route
    this._maze = new MazeField(this._seed);
    this._route = [];
    this._sBar = 0; this._rate = 1; this._s = 0;
    this._lockS = -Infinity;
    this._planFrom(0, 0, 1, 0, this._rate, null);

    // state
    let seed = 1234567;
    const rnd = () => { seed = (seed * 1664525 + 1013904223) >>> 0; return seed / 4294967296; };
    this._time = 0; this._beats = 0; this._lastBeats = null;
    this._lastBeatPhase = null; this._lastBeatIndex = null; this._lastBarPhase = null; this._lastPhrasePhase = null;
    this._beatCount = 0; this._barCount = 0; this._phraseCount = 0; this._beatPeriod = 60 / SYNTH_BPM;
    this._level = new Float32Array(BANDS); this._peak = new Float32Array(BANDS); this._peakHold = new Float32Array(BANDS); this._target = new Float32Array(BANDS);
    this._noisePhase = new Float32Array(BANDS); this._noiseRate = new Float32Array(BANDS);
    for (let b = 0; b < BANDS; b++) { this._noisePhase[b] = rnd() * 6.28; this._noiseRate[b] = 0.4 + 0.9 * rnd(); }
    this._kick = 0; this._pulse = 0; this._edgeFlash = 0; this._scroll = 0;
    this._dropAt = -99; this._hallAt = -99; this._hallPending = false;
    this._palette = [0x2bd6ff, 0xff2bd6, 0x8a2bff, 0x2bff9e].map((c) => vdjColor(c)); this._colorIndex = 0; this._lastPaletteRef = undefined;
    this._kickCam = { zoom: 1, roll: 0 };
    this._coverUrl = undefined; this._coverTex = null;
    this._pos = new THREE.Vector3(); this._dir = new THREE.Vector2(0, 1); this._roll = 0;
    this._minClear = Infinity; this._turnLog = []; this._lastCornerS = null;
    this._setAccent();
  }

  // --- the route ------------------------------------------------------------------
  // Depth-first over the junctions from (X, Z), arriving with heading hin at
  // path length s: every node's leaving heading. A turn only where canTurn(s);
  // the plan must END heading north or east (always continuable).
  _dfs(X, Z, hin, s, sEnd, canTurn) {
    if (s >= sEnd) return (hin === 0 || hin === 1) ? [] : null;
    const mz = this._maze, opts = [];
    const straight = hin, left = (hin + 1) % 4, right = (hin + 3) % 4;
    if (canTurn(s)) {
      const turns = hash(X, Z, 131 + this._seed) < 0.5 ? [left, right] : [right, left];
      if (hash(X, Z, 151 + this._seed) < TURN_P) opts.push(...turns, straight); else opts.push(straight, ...turns);
    } else opts.push(straight);
    for (const d of opts) {
      if (!mz.exit(X, Z, d)) continue;
      const rest = this._dfs(X + DIRS[d][0], Z + DIRS[d][1], d, s + J, sEnd, canTurn);
      if (rest) return [d, ...rest];
    }
    return null;
  }

  // Replan from the junction (X, Z) reached at path length s with heading hin,
  // over PLAN_CELLS: turns only on the junctions of a first beat at rate r.
  // At x2 and x4 (the montee) the straight runs this asks for (8 and 16 cells)
  // are often missing: the plan is then RELAXED to any junction - still on a
  // beat, since a junction comes every 4 / rate beats.
  _planFrom(X, Z, hin, s, r, keep) {
    const sEnd = s + PLAN_CELLS;
    const barCells = J * r;
    const strict = (t) => ((t - this._sBar) % barCells + barCells) % barCells === 0;
    let plan = this._dfs(X, Z, hin, s, sEnd, strict);
    this._relaxed = false;
    if (!plan) { plan = this._dfs(X, Z, hin, s, sEnd, () => true); this._relaxed = true; }
    if (!plan) plan = [this._maze.exit(X, Z, hin) ? hin : [0, 1, 2, 3].find((d) => this._maze.exit(X, Z, d))];
    const route = keep || [];
    let x = X, z = Z, h = hin, t = s;
    for (const d of plan) {
      route.push({ X: x, Z: z, s: t, hin: h, hout: d });
      x += DIRS[d][0]; z += DIRS[d][1]; h = d; t += J;
    }
    route.push({ X: x, Z: z, s: t, hin: h, hout: h });       // the horizon: provisional
    this._route = route;
  }

  // every bar: commit the nodes up to the bar's junction (and a locked hall
  // node), plan the rest again at the bar's rate
  _replan() {
    const keepS = Math.max(this._sBar, this._lockS);
    const keep = this._route.filter((n) => n.s <= keepS);
    const last = keep[keep.length - 1];
    if (!last) { this._planFrom(0, 0, 1, this._sBar, this._rate, null); return; }
    keep.pop();
    // the last kept node keeps its leaving heading: plan from the next one
    const d = last.hout;
    keep.push(last);
    this._planFrom(last.X + DIRS[d][0], last.Z + DIRS[d][1], d, last.s + J, this._rate, keep);
  }

  _nodeAt(s) {
    const R = this._route;
    for (let i = R.length - 1; i >= 0; i--) if (R[i].s <= s) return i;
    return 0;
  }

  // the camera on the route at path length s: position (cells, absolute,
  // cell centres at integer + 0.5), heading, turn progress
  _poseAt(s) {
    const R = this._route, i = this._nodeAt(s), n = R[i];
    const c = (k) => [R[k].X * J + 0.5, R[k].Z * J + 0.5];
    let [x, z] = c(i);
    const ds = s - n.s;
    let dir = DIRS[n.hout], roll = 0;
    const arc = (node, t) => {
      // quarter circle of radius TURN_R through the junction centre's corner
      const [cx, cz] = [node.X * J + 0.5, node.Z * J + 0.5];
      const di = DIRS[node.hin], dO = DIRS[node.hout];
      const ox = cx - di[0] * TURN_R + dO[0] * TURN_R, oz = cz - di[1] * TURN_R + dO[1] * TURN_R;
      const th = t * Math.PI / 2;
      const px = ox + TURN_R * (-dO[0] * Math.cos(th) + di[0] * Math.sin(th));
      const pz = oz + TURN_R * (-dO[1] * Math.cos(th) + di[1] * Math.sin(th));
      const tx = dO[0] * Math.sin(th) + di[0] * Math.cos(th), tz = dO[1] * Math.sin(th) + di[1] * Math.cos(th);
      const left = ((node.hout - node.hin + 4) % 4) === 1;
      return { x: px, z: pz, dx: tx, dz: tz, roll: (left ? 1 : -1) * TURN_ROLL * Math.sin(Math.PI * t) };
    };
    if (ds < TURN_R && n.hin !== n.hout) {
      const a = arc(n, (ds + TURN_R) / (2 * TURN_R));
      return { x: a.x, z: a.z, dx: a.dx, dz: a.dz, roll: a.roll };
    }
    const nx = R[i + 1];
    if (nx && nx.s - s < TURN_R && nx.hin !== nx.hout) {
      const a = arc(nx, 1 - (nx.s - s + TURN_R) / (2 * TURN_R));
      return { x: a.x, z: a.z, dx: a.dx, dz: a.dz, roll: a.roll };
    }
    x += dir[0] * ds; z += dir[1] * ds;
    return { x, z, dx: dir[0], dz: dir[1], roll };
  }

  // the drop hall: the segment leaving junction node n, 2 cells each side
  _placeHall(n) {
    const d = DIRS[n.hout];
    const x0 = n.X * J, z0 = n.Z * J;
    const x1 = x0 + d[0] * J, z1 = z0 + d[1] * J;
    const ax = Math.min(x0, x1), bx = Math.max(x0, x1), az = Math.min(z0, z1), bz = Math.max(z0, z1);
    const h = d[0] !== 0 ? [ax, az - HALL_HALF, bx, bz + HALL_HALF] : [ax - HALL_HALF, az, bx + HALL_HALF, bz];
    this._maze.hall = h;
    this._u.uHall.value.set(h[0], h[1], h[2], h[3]);
    this._u.uHallOn.value = 1;
    this._hallNodeS = n.s;
    this._lockS = Math.max(this._lockS, n.s);
  }

  // clearance (probe): the camera's distance to the nearest solid cell, xz
  _checkClearance() {
    const p = this._pos, mz = this._maze;
    let best = Infinity;
    const cx0 = Math.floor(p.x), cz0 = Math.floor(p.z);
    for (let cz = cz0 - 1; cz <= cz0 + 1; cz++) for (let cx = cx0 - 1; cx <= cx0 + 1; cx++) {
      if (mz.open(cx, cz)) continue;
      const dx = Math.max(0, cx - p.x, p.x - (cx + 1)), dz = Math.max(0, cz - p.z, p.z - (cz + 1));
      best = Math.min(best, (dx === 0 && dz === 0) ? -1 : Math.hypot(dx, dz));
    }
    this._clearance = best;
    if (best < this._minClear) this._minClear = best;
  }

  _setAccent() {
    const P = this._palette, a = P[this._colorIndex % P.length];
    // the second colour: the palette colour furthest in hue (R4: >= 0.25 apart)
    const hsl = {}, h0 = (new THREE.Color(a).getHSL(hsl), hsl.h);
    let best = null, bd = -1;
    for (const c of P) { new THREE.Color(c).getHSL(hsl); let dh = Math.abs(hsl.h - h0); dh = Math.min(dh, 1 - dh); if (dh > bd) { bd = dh; best = c; } }
    if (bd < 0.25) { const c = new THREE.Color(a); c.getHSL(hsl); c.setHSL((hsl.h + 0.5) % 1, hsl.s, hsl.l); best = c.getHex(); }
    vdjColor(a, this._u.uAccent.value, 1.0);
    vdjColor(best, this._u.uAccent2.value, 1.0);
  }
  _applyPalette(pal) {
    const v = pal && pal.vividColors && pal.vividColors.length ? pal.vividColors : null;
    if (v) this._palette = v.slice(0, 4).map((c) => vdjColor(c));
    this._setAccent();
  }
  _loadCover(profile) {
    if (profile.artworkUrl === this._coverUrl) return;
    this._coverUrl = profile.artworkUrl;
    this._u.uCover.value = BLANK; this._u.uHasCover.value = 0;
    if (this._coverTex) { this._coverTex.dispose(); this._coverTex = null; }
    if (!profile.artworkUrl) return;
    const self = this, url = profile.artworkUrl;
    loadArtworkTexture(url, { wrap: THREE.ClampToEdgeWrapping, mipmaps: true }, (tex) => {
      if (!self._scene || url !== self._coverUrl) { tex.dispose(); return; }
      self._coverTex = tex; self._u.uCover.value = tex; self._u.uHasCover.value = 1;
    }, () => {});
  }

  onTrackChange(profile) { this._loadCover(profile); }

  // --- rhythm ---------------------------------------------------------------------
  _onBeat() { this._beatCount++; this._kick = 1; this._pulse = 1; }

  _onBar(beatsToDrop) {
    this._barCount++;
    // the bar just finished covered 4 x rate cells: the junction of this first beat
    if (!this._stop) this._sBar += J * this._rate;
    // the rate for this bar: x2 then x4 in the montee, x1 from the drop
    const inDrop = this._time - this._dropAt < 4 * 4 * this._beatPeriod;
    // (a drop on THIS first beat: back to x1 already)
    this._rate = !inDrop && beatsToDrop != null && beatsToDrop >= 0.5 && beatsToDrop < 32 ? (beatsToDrop < 12 ? 4 : 2) : 1;
    // did the camera turn at this junction? no: flash the corridor's edges
    const n = this._route[this._nodeAt(this._sBar)];
    if (n && n.s === this._sBar && n.hin === n.hout) this._edgeFlash = 1;
    this._replan();
    // a drop on the next first beat: the hall on the segment it opens
    if (beatsToDrop != null && beatsToDrop > 0.5 && beatsToDrop <= 4.5 && !this._hallPending) {
      const nx = this._route.find((m) => m.s === this._sBar + J * this._rate);
      if (nx) { this._placeHall(nx); this._hallPending = true; }
    }
  }

  _onPhrase() {
    this._phraseCount++;
    this._colorIndex++;
    this._setAccent();
    this._u.uPhrase.value = this._phraseCount;
  }

  _onDrop(director) {
    this._dropAt = this._time;
    if (this._comfort.requestFlash(this._time) && director) director.flash = Math.max(director.flash || 0, VDJ_DROP_FLASH);
    // no warning: the hall on the segment after the next junction (the flash
    // hides its appearance)
    if (!this._hallPending) {
      const nx = this._route.find((m) => m.s >= this._s + J);
      if (nx) this._placeHall(nx);
    }
    this._hallPending = false;
    // the rate is NOT reset here: the drop can be seen a frame before the
    // first beat, and a rate changed mid-bar moves the camera back along the
    // route. _onBar sets x1 for the bar the drop opens.
  }

  // --- the equaliser (Cyberflight's) ---------------------------------------------
  _equaliser(bands, energy, dt) {
    const bv = bands ? [bands.low, bands.mid, bands.high] : null;
    for (let b = 0; b < BANDS; b++) {
      const x = b / (BANDS - 1);
      let v;
      if (bv) {
        let ws = 0; v = 0;
        for (let i = 0; i < 3; i++) { const d = (x - BAND_CENTRES[i]) / BAND_SIGMA; const w = Math.exp(-d * d); v += w * bv[i]; ws += w; }
        v = ws > 0 ? v / ws : 0;
      } else {
        v = energy * (0.58 - 0.28 * x);
      }
      this._noisePhase[b] += dt * this._noiseRate[b];
      v *= 1 + DECORR * Math.sin(this._noisePhase[b]) + (bv ? 0 : 0.25 * Math.sin(this._noisePhase[b] * 1.7 + b));
      v += KICK_SPIKE * this._kick * (1 - 0.6 * x);
      this._target[b] = clamp01(v);
    }
    this._kick = Math.max(0, this._kick - dt * 8);
    for (let b = 0; b < BANDS; b++) {
      const t = this._target[b];
      let v = this._level[b];
      if (t >= v) v = t; else v = Math.max(t, v - FALL_PER_S * dt);
      this._level[b] = v;
      if (v >= this._peak[b]) { this._peak[b] = v; this._peakHold[b] = PEAK_HOLD_S; }
      else if (this._peakHold[b] > 0) this._peakHold[b] -= dt;
      else this._peak[b] = Math.max(v, this._peak[b] - PEAK_FALL_PER_S * dt);
    }
    const d = this._levelsData;
    for (let b = 0; b < BANDS; b++) {
      d[b * 4] = Math.round(clamp01(this._level[b]) * 255); d[b * 4 + 1] = Math.round(clamp01(this._peak[b]) * 255);
    }
    this._levelsTex.needsUpdate = true;
  }

  update(deltaTime, profile) {
    const paused = profile.playbackState !== 'PLAYING';
    const dt = Math.min(deltaTime, 0.05);
    const director = profile.director;
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || SYNTH_BPM;
    this._beatPeriod = 60 / bpm;
    if (profile.artworkPalette !== this._lastPaletteRef) { this._lastPaletteRef = profile.artworkPalette; this._applyPalette(profile.artworkPalette); }
    this._loadCover(profile);
    if (paused) return;            // the frame holds
    this._time += dt;
    const bands = director && director.hasReliableGrid && director.bands ? director.bands : null;
    const energy = director && director.intensity != null ? clamp01(director.intensity) : 0.65;

    // the beat clock
    const beatPhase = director && director.beat != null ? director.beat : profile.beatPhase;
    let beatEdge = false;
    if (beatPhase != null) {
      if (this._lastBeatPhase != null && beatPhase < this._lastBeatPhase) beatEdge = true;
      const bi = profile.beatIndex;
      if (bi != null && this._lastBeatIndex != null) this._beats += Math.max(0, (bi - this._lastBeatIndex) + (beatPhase - this._lastBeatPhase));
      else this._beats += dt / this._beatPeriod;
      this._lastBeatIndex = bi != null ? bi : null;
      this._lastBeatPhase = beatPhase;
    } else {
      const before = Math.floor(this._beats);
      this._beats += dt / this._beatPeriod;
      if (Math.floor(this._beats) !== before) beatEdge = true;
    }
    const barsNow = Math.floor(this._beats / 4);
    const barsBefore = this._barsBefore != null ? this._barsBefore : barsNow;
    this._barsBefore = barsNow;
    const ev = director && director.nextEvent;
    const dropMs = ev && ev.type === 'drop' && ev.msUntil != null ? ev.msUntil : null;
    const beatsToDrop = dropMs != null && dropMs > 0 ? dropMs / 1000 / this._beatPeriod : null;
    if (beatEdge) this._onBeat();
    // the bar: from the grid, or every 4 beats
    let barPhase = director && director.bar != null ? director.bar : profile.barPhase;
    let barEdge = false;
    if (barPhase != null) { if (this._lastBarPhase != null && barPhase < this._lastBarPhase) barEdge = true; this._lastBarPhase = barPhase; }
    else { barPhase = (this._beats % 4) / 4; if (barsNow !== barsBefore) barEdge = true; }
    if (barEdge) this._onBar(beatsToDrop);
    const phrasePhase = director && director.phrase != null ? director.phrase : profile.phrasePhase;
    if (phrasePhase != null) { if (this._lastPhrasePhase != null && phrasePhase < this._lastPhrasePhase) this._onPhrase(); this._lastPhrasePhase = phrasePhase; }
    else if (beatEdge && this._beatCount % 32 === 0) this._onPhrase();
    // the drop, on the frame that crosses the mark
    const nowMs = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
    if (dropMs != null && dropMs <= dt * 500 + 30 && nowMs != null) {
      const key = Math.round((nowMs + dropMs) / 100);
      // one drop per mark: the key alone can split across two 100 ms buckets
      if (key !== this._lastDropKey && this._time - this._dropAt > 2) { this._lastDropKey = key; this._onDrop(director); }
    }

    // the path length: the bar's junction + rate x 4 cells x the bar's phase
    // (a junction on every first beat, by construction)
    if (!this._stop) this._s = this._sBar + J * this._rate * clamp01(barPhase);
    // the route always reaches past the camera
    if (this._route[this._route.length - 1].s < this._s + 2 * J) this._replan();

    // the hall's ceiling: opens over a beat once the camera is in it, after a drop
    const inHall = this._maze.hall && this._maze.inHall(Math.floor(this._pos.x), Math.floor(this._pos.z));
    const sinceDrop = (this._time - this._dropAt) / this._beatPeriod;
    const openTarget = inHall && sinceDrop < 32 ? 1 : (this._u.uCeilOpen.value > 0 && sinceDrop < 32 ? 1 : 0);
    const co = this._u.uCeilOpen.value;
    this._u.uCeilOpen.value = openTarget > co ? Math.min(1, co + dt / this._beatPeriod) : Math.max(0, co - dt / (4 * this._beatPeriod));

    this._equaliser(bands, energy, dt);
    this._pulse = Math.max(0, this._pulse - dt / (this._beatPeriod * 0.35));
    this._edgeFlash = Math.max(0, this._edgeFlash - dt / (this._beatPeriod * 0.5));
    this._scroll += dt / this._beatPeriod * (1 + this._rate);
    this._kickCam = vdjCameraKick(barPhase, this._barCount);
    this._post.update(vdjBeatChroma(beatPhase != null ? beatPhase : this._beats % 1, this._beatPeriod));

    const u = this._u;
    u.uPulse.value = this._pulse;
    u.uEdgeFlash.value = this._edgeFlash;
    u.uScroll.value = this._scroll;
    u.uBeats.value = this._beats;
    u.uForce.value = this._forcedPanel != null ? this._forcedPanel : (this._rate > 1 ? PANEL_NAMES.arrows : -1);
    const est = 0.09 + (director && director.flash ? director.flash * 0.8 : 0);
    this._qu.uGain.value = this._comfort.measure(est, dt);
  }

  _pose() {
    const p = this._poseAt(this._s);
    this._pos.set(p.x, CAM_Y, p.z);
    this._dir.set(p.dx, p.dz).normalize();
    this._roll = p.roll;
    // the turn log (probe): the beat phase when the camera crosses a corner
    const ni = this._nodeAt(this._s), n = this._route[ni];
    if (n && n.hin !== n.hout && this._lastCornerS !== n.s && this._s >= n.s) {
      this._lastCornerS = n.s;
      this._turnLog.push({ s: n.s, bar: this._lastBarPhase != null ? +this._lastBarPhase.toFixed(3) : null, beats: +this._beats.toFixed(2), rate: this._rate });
      if (this._turnLog.length > 40) this._turnLog.shift();
    }
    const cam = this._cam, u = this._u, N = this._n;
    const ox = Math.floor(p.x), oz = Math.floor(p.z);
    u.uOrigin.value.set(ox, oz);
    u.uWinBase.value.set(ox - N / 2, oz - N / 2);
    u.uFwd.value.copy(this._dir);
    cam.position.set(p.x - ox, CAM_Y, p.z - oz);
    cam.up.set(0, 1, 0);
    cam.lookAt(p.x - ox + p.dx, CAM_Y, p.z - oz + p.dz);
    cam.rotateZ(this._roll + this._kickCam.roll);
    cam.zoom = this._kickCam.zoom;
    cam.updateProjectionMatrix();
    cam.updateMatrixWorld();
    this._checkClearance();
  }

  _render(renderer) {
    renderer.getDrawingBufferSize(this._size);
    if (this._rt.width !== this._size.x || this._rt.height !== this._size.y) this._rt.setSize(this._size.x, this._size.y);
    this._cam.aspect = this._size.x / Math.max(1, this._size.y);
    this._pose();
    const prev = renderer.getRenderTarget();
    const cc = new THREE.Color(); renderer.getClearColor(cc); const ca = renderer.getClearAlpha();
    renderer.setRenderTarget(this._rt);
    renderer.setClearColor(0x000000, 1);
    renderer.clear(true, true, false);
    renderer.render(this._inner, this._cam);
    renderer.setClearColor(cc, ca);
    renderer.setRenderTarget(prev);
  }

  getProbe() {
    const R = this._route;
    let deadEnds = 0;
    for (const n of R.slice(0, -1)) if (!this._maze.exit(n.X, n.Z, n.hout)) deadEnds++;
    return {
      t: +this._time.toFixed(2), beats: +this._beats.toFixed(2), s: +this._s.toFixed(3), sBar: this._sBar, rate: this._rate,
      pos: [+this._pos.x.toFixed(3), +this._pos.z.toFixed(3)], dir: [+this._dir.x.toFixed(2), +this._dir.y.toFixed(2)],
      clearance: +(this._clearance || 0).toFixed(3), minClear: +this._minClear.toFixed(3),
      route: R.map((n) => [n.X, n.Z, n.s, n.hin, n.hout]), deadEnds, relaxed: !!this._relaxed,
      turns: this._turnLog.slice(), hall: this._maze.hall, ceil: +this._u.uCeilOpen.value.toFixed(2),
      levels: Array.from(this._level).map((v) => +v.toFixed(2)), gain: +this._comfort.gain.toFixed(3)
    };
  }

  dispose() {
    if (this._quad) { this._quad.onBeforeRender = null; if (this._scene) this._scene.remove(this._quad); this._quad.geometry.dispose(); this._quad.material.dispose(); }
    if (this._geo) this._geo.dispose();
    if (this._rt) this._rt.dispose();
    if (this._levelsTex) this._levelsTex.dispose();
    if (this._u) { this._u.uCover.value = BLANK; this._u.uHasCover.value = 0; this._u.uHallOn.value = 0; this._u.uCeilOpen.value = 0; }
    if (this._coverTex) { this._coverTex.dispose(); this._coverTex = null; }
    this._scene = null;
  }
}

// exposed for the tests: the maze on the CPU and its GLSL twin
export { MazeField, MAZE_GLSL };
